import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../appRedux/store";

import * as dashboardServices from "../../services/dashboard";
import { CustomNotification } from "../../util/CustomNotification";
import { TableHelper } from "../../util/TableHelper";
import useTablePagination from "../../hooks/useTablePagination";
import useValue from "../../hooks/useValue";
import styles from "./index.module.less";
import { DefaultPagination } from "../../constants/generalConstants";

import type { ClientParkingAvailability, ClientParkingAvailabilityResponse } from "../../shared/interface/dashboard.interface";
import type { ColumnsType } from "antd/lib/table";
import { Tag } from "antd";

export const useClientParkingAvailabilityList = () => {
  /* CONSTANTS */
  const valueSorter = (key: keyof ClientParkingAvailability) => ({ compare: TableHelper.valueCompare(key) });
  const digitSorter = (key: keyof ClientParkingAvailability) => ({ compare: TableHelper.digitSorter(key) });
  const initialColumns: ColumnsType<any> = [
    {
      align: "left",
      title: "Sr No.",
      dataIndex: "sr_no",
      key: "sr_no",
      render: TableHelper.renderNumberValue,
      sorter: valueSorter("sr_no"),
      width: "5%"
    },
    {
      align: "left",
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
      render: TableHelper.renderText,
      sorter: valueSorter("client_name")
    },
    {
      align: "left",
      title: "Parking Quota",
      dataIndex: "allocated",
      key: "allocated",
      render: TableHelper.renderNumberValue,
      sorter: digitSorter("allocated")
    },
    {
      align: "left",
      title: "Available",
      dataIndex: "available",
      key: "available",
      render: TableHelper.renderNumberValue,
      fixed: "right",
      onCell: (record) => {
        const className = record.available > 0 ? styles["green"] : styles["red"];
        return {
          className
        };
      },
      sorter: digitSorter("available")
    }
  ];

  /* STATE */
  const [tableData, setTableData] = useState<ClientParkingAvailability[]>([]);
  const [columns, setColumns] = useState<ColumnsType<any>>(initialColumns);
  const [filteredData, setFilteredData] = useState<ClientParkingAvailability[]>([]);
  const [searchedText, setSearchedText] = useState<string>("");
  const { masterFilters } = useSelector(({ common }: RootState) => common);
  const { value: autoRefresh, handleValue: handleAutoRefresh } = useValue(false);

  /* HOOK */
  const { pagination, handleChangePagination, handleChangeTotalSize } = useTablePagination(0);
  const { value: loading, handleEnableValue: handleEnableLoading, handleDisableValue: handleDisableLoading } = useValue(false);

  /* REF */
  const autoRefreshRef = useRef(autoRefresh);

  /* HELPER FUNCTIONS */
  const formatTableData = (dashboardResponse: ClientParkingAvailabilityResponse) => {
    /* start index is used to maintain the srNo for next page. */
    const startIndex = ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 6);
    const formattedResponse: Record<string, ClientParkingAvailability> = {};

    dashboardResponse?.data?.forEach((customerClientStats) => {
      const columnName = customerClientStats.vehicle_type_name;
      if (!customerClientStats.client_name) return;

      const foundItem = formattedResponse[customerClientStats.client_name];
      if (foundItem) {
        // @ts-ignore
        foundItem[columnName] = { parked: customerClientStats.parked, key_fob_allocation_count: customerClientStats.key_fob_allocation_count };
        foundItem.available -= customerClientStats.parked ?? 0;
      } else {
        formattedResponse[customerClientStats.client_name] = {
          ...customerClientStats,
          [columnName]: { parked: customerClientStats.parked, key_fob_allocation_count: customerClientStats.key_fob_allocation_count },
          sr_no: startIndex + Object.keys(formattedResponse).length + 1,
          available: customerClientStats.allocated - (customerClientStats.parked || 0)
        };
      }
    });

    return Object.values(formattedResponse);
  };

  /* HANDLER */
  const handleVehicleTypeWiseParkedColumns = (prevState: ColumnsType<any>, vehicleTypeMasterList: any[]) => {
    const vehicleTypeWiseParkedColumns = {
      title: "Total Parked / Total Allocated",
      children: [] as ColumnsType<any>
    };

    vehicleTypeMasterList.forEach((item) => {
      const columnName = item.vehicle_type_name;

      vehicleTypeWiseParkedColumns.children.push({
        title: columnName,
        dataIndex: columnName,
        key: columnName,
        align: "center",
        render: (record): any => {
          if (!record.parked && !record.key_fob_allocation_count)
            return (
              <Tag color="error" style={{ fontWeight: "normal" }}>
                NA
              </Tag>
            );
          return (
            <Tag color="success" style={{ fontWeight: "bolder" }}>
              {record.parked} / {record.key_fob_allocation_count}
            </Tag>
          );
        }
      });
    });

    /* last item (available column) of prevState is moved at last position before concat with vehicleTypeWiseParkedColumns*/
    return prevState.slice(0, -1).concat(vehicleTypeWiseParkedColumns).concat(prevState.slice(-1));
  };

  const handleChangeSearch = (value: string) => {
    const trimmedValue = value?.replace(/^\s+/, "");
    const data = TableHelper.getFilterDataSource(tableData, ["client_name"], trimmedValue);
    setFilteredData(data);
    setSearchedText(trimmedValue);
  };

  const fetchAllData = async () => {
    try {
      if (!masterFilters?.customerId || !masterFilters?.locationId) return;

      handleEnableLoading();
      setColumns(initialColumns);

      const page = pagination?.current || DefaultPagination.PAGE;
      const pageSize = pagination?.pageSize || DefaultPagination.PAGE_SIZE;

      const dashboardResponse = await dashboardServices.getClientParkingAvailabilityStats(masterFilters?.customerId, masterFilters?.locationId, page, pageSize);
      setColumns((prevState) => handleVehicleTypeWiseParkedColumns(prevState, dashboardResponse.vehicleTypeMasterList));

      const formattedResponse = formatTableData(dashboardResponse);

      handleChangeTotalSize(dashboardResponse?.count || 0);
      setTableData(formattedResponse);
      setFilteredData(formattedResponse);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableLoading();
    }
  };

  const handleRefreshList = () => fetchAllData();

  /* EFFECTS */
  useEffect(() => {
    fetchAllData();

    // eslint-disable-next-line
  }, [pagination?.current, pagination?.pageSize, masterFilters?.customerId, masterFilters?.locationId]);

  useEffect(() => {
    const INTERVAL = 10_000;
    const totalRows = pagination.total;
    const pageSize = pagination.pageSize;
    const currentPage = pagination.current;

    if (!totalRows || !pageSize || !currentPage) return;

    const totalPages = Math.ceil(totalRows / pageSize);

    const interval = setInterval(() => {
      if (!autoRefreshRef.current) return;
      const nextPage = currentPage + 1;

      /* if totalPages > 1 we move to next page, if we reach last page we move back to first page. */
      if (totalPages > 1) {
        if (nextPage <= totalPages) {
          pagination.current = nextPage;
          handleChangePagination(pagination);
        } else {
          pagination.current = 1;
          handleChangePagination(pagination);
        }
      } else if (totalPages === 1) {
        /* if total pages == 1 we refresh the same page. */
        handleRefreshList();
      }
    }, INTERVAL);

    return () => clearInterval(interval);

    // eslint-disable-next-line
  }, [tableData]);

  /* autoRefreshRef is used for variable autoRefresh as autoRefresh is being used in an interval, and because of closures directly reading autoRefresh will give the last value
     when setInterval was called.
  */
  useEffect(() => {
    autoRefreshRef.current = autoRefresh;
  }, [autoRefresh]);

  return { loading, handleRefreshList, searchedText, handleChangeSearch, filteredData, pagination, handleChangePagination, columns, autoRefresh, handleAutoRefresh };
};
